import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { loadImageForEditing } from '../utils/imageCompress';
import AvatarCropper from './AvatarCropper';
import './AvatarLightbox.css';
import PersonIcon from './PersonIcon';

// ── PROFILE PICTURE, FULL SIZE ───────────────────────────────────────────────
// Tapping an avatar opens it big and centred, with change / delete underneath
// when it is your own — the behaviour people already know from Instagram.
//
// `editable` is what separates the two callers: the dashboard header passes it,
// the public profile does not, so a visitor gets the viewer and no controls.
//
// PORTALLED TO document.body. The header this opens from sits inside a grid
// with transformed and blurred ancestors, and either of those makes a
// `position: fixed` child resolve against THAT element instead of the viewport
// — the overlay would cover the header strip rather than the page.
export default function AvatarLightbox({ src, name, editable = false, onChange, onClose }) {
  const { t } = useT();
  const fileRef = useRef(null);
  const closeRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Decoded image awaiting a crop; nothing is saved until the crop is accepted.
  const [pending, setPending] = useState(null);

  // Escape closes, like every other overlay in the app. Focus moves to the
  // close button so a keyboard user is inside the dialog, not still behind it.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';                       // allow re-picking the same file
    if (!file) return;
    setError('');
    if (!/^image\//.test(file.type)) { setError(t('ds_err_avatar')); return; }
    setBusy(true);
    try {
      // Decode only. The crop editor needs real pixel dimensions to work out
      // its scales, and compressing first throws away the resolution that
      // zooming in depends on. Same pipeline as AvatarPicker — deliberately, so
      // a picture chosen here and one chosen in Settings come out identical.
      setPending(await loadImageForEditing(file));
    } catch {
      setError(t('ds_err_avatar'));
    } finally {
      setBusy(false);
    }
  }

  async function save(url) {
    setBusy(true);
    setError('');
    try {
      await onChange(url);
      onClose();
    } catch {
      setError(t('ds_err_avatar'));
      setBusy(false);
    }
  }

  return createPortal(
    <div className="avl" role="dialog" aria-modal="true" aria-label={t('ds_avatar')} onClick={onClose}>
      <button
        ref={closeRef}
        type="button"
        className="avl-close"
        onClick={onClose}
        aria-label={t('cancel')}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Clicks inside the card must not reach the backdrop's close handler. */}
      <div className="avl-box" onClick={(e) => e.stopPropagation()}>
        <div className="avl-figure">
          {src
            ? <img className="avl-img" src={src} alt={name || ''} />
            : <PersonIcon className="avl-ph" />}
        </div>

        {error && <p className="avl-err">{error}</p>}

        {editable && (
          confirmDelete ? (
            // Removing a picture cannot be undone from here — the file is gone
            // from the browser by now — so it asks once.
            <div className="avl-confirm">
              <p className="avl-confirm-text">{t('avl_delete_confirm')}</p>
              <div className="avl-actions">
                <button type="button" className="avl-btn" disabled={busy}
                  onClick={() => setConfirmDelete(false)}>
                  {t('cancel')}
                </button>
                <button type="button" className="avl-btn avl-btn--danger" disabled={busy}
                  onClick={() => save('')}>
                  {t('delete_tooltip')}
                </button>
              </div>
            </div>
          ) : (
            <div className="avl-actions">
              <button type="button" className="avl-btn" disabled={busy}
                onClick={() => fileRef.current?.click()}>
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                  strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9a2 2 0 0 1 2-2h1.6a2 2 0 0 0 1.7-.9l.8-1.2a2 2 0 0 1 1.7-.9h2.4a2 2 0 0 1 1.7.9l.8 1.2a2 2 0 0 0 1.7.9H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <circle cx="12" cy="13" r="3.2" />
                </svg>
                {busy ? '…' : (src ? t('avl_change') : t('avl_add'))}
              </button>

              {src && (
                <button type="button" className="avl-btn avl-btn--danger" disabled={busy}
                  onClick={() => setConfirmDelete(true)}>
                  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                    strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                  </svg>
                  {t('delete_tooltip')}
                </button>
              )}
            </div>
          )
        )}

        <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} />
      </div>

      {pending && (
        <AvatarCropper
          image={pending}
          onCancel={() => { pending.revoke(); setPending(null); }}
          onDone={(dataUrl) => { pending.revoke(); setPending(null); save(dataUrl); }}
        />
      )}
    </div>,
    document.body,
  );
}
